import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogContent,
  Fade,
  Stack,
} from "@mui/material";
import { Menu as MenuIcon, Close as CloseIcon } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're on the home page
  const isHomePage = location.pathname === "/";

  // Determine if border should be shown
  const shouldShowBorder = isHomePage ? isScrolled : true;

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogoClick = () => {
    navigate("/");
  };

  const handleNavClick = (link) => {
    if (link.type === "route") {
      navigate(link.href);
    } else {
      // For anchor links, scroll to the section (only works on landing page)
      const element = document.querySelector(link.href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const navLinks = [
    { label: "Home", href: "/", type: "route" },
    { label: "Stays", href: "/properties", type: "route" },
    { label: "Experiences", href: "/experiences", type: "route" },
    { label: "About", href: "/about", type: "route" },
  ];

  const drawer = (
    <Dialog
      open={mobileOpen}
      onClose={handleDrawerToggle}
      fullScreen
      TransitionComponent={Fade}
      transitionDuration={300}
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "#fff",
          backdropFilter: "none",
        },
      }}
    >
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          padding: 0,
          position: "relative",
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            position: "absolute",
            top: 20,
            right: 20,
            color: theme.palette.text.primary,
            backgroundColor: "rgba(0, 0, 0, 0.05)",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Logo */}
        <Box sx={{ mb: 6 }}>
          <img
            src="/wordmark.svg"
            alt="Aspire Ventures Logo"
            style={{ height: 60, cursor: "pointer" }}
            onClick={handleLogoClick}
          />
        </Box>

        {/* Navigation Links */}
        <Stack spacing={4} alignItems="center" sx={{ mb: 6 }}>
          {navLinks.map((link, index) => (
            <Button
              key={link.label}
              onClick={() => {
                handleDrawerToggle();
                handleNavClick(link);
              }}
              sx={{
                color: theme.palette.primary.light,
                textTransform: "none",
                fontWeight: 500,
                fontSize: "1.5rem",
                fontFamily: "Monument-Private",
                py: 1,
                px: 3,
                transition: "all 0.3s ease",
                "&:hover": {
                  color: theme.palette.primary.main,
                  backgroundColor: "transparent",
                  transform: "scale(1.05)",
                },
              }}
            >
              {link.label}
            </Button>
          ))}
        </Stack>

        {/* Contact Button */}
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={() => {
            handleDrawerToggle();
            navigate("/contact");
          }}
          sx={{
            fontWeight: 600,
            px: 4,
            py: 2,
            width: "90%",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateY(-2px)",
            },
          }}
        >
          Contact Us
        </Button>
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "#fff",
          borderBottom: shouldShowBorder ? "1px solid rgba(0, 0, 0, 0.1)" : "none",
          boxShadow: "none",
          color: theme.palette.text.primary,
          transition: "border-bottom 0.3s ease",
        }}
      >
        <Toolbar
          sx={{ justifyContent: "space-between", px: { xs: 4, sm: 3, md: 4 } }}
        >
          {/* Logo */}
          <img
            src="/wordmark.svg"
            alt="Aspire Ventures Logo"
            style={{ height: 40, cursor: "pointer" }}
            onClick={handleLogoClick}
          />

          {/* Desktop Navigation */}
          {!isMobile && (
            <>
              <Box sx={{ display: "flex", gap: 3 }}>
                {navLinks.map((link) => (
                  <Button
                    key={link.label}
                    onClick={() => handleNavClick(link)}
                    sx={{
                      color: theme.palette.primary.light,
                      textTransform: "none",
                      fontWeight: 500,
                      fontSize: "0.8rem",
                      fontFamily: "Monument-Private",
                      "&:hover": {
                        color: theme.palette.primary.main,
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {link.label}
                  </Button>
                ))}
              </Box>

              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate("/contact")}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                }}
              >
                Contact Us
              </Button>
            </>
          )}

          {/* Mobile Menu Icon */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ color: theme.palette.text.primary }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Full-Screen Menu */}
      {drawer}

      {/* Spacer to prevent content from hiding behind fixed navbar */}
      <Toolbar />
    </>
  );
};

export default Navbar;
